import { useEffect, useRef } from "react";
import { Chess } from "chess.js";
import { ScrollArea } from "@/components/ui/scroll-area";

export const MoveHistory = ({ moves, playerColor, currentMoveIndex, onMoveSelect }) => {
  const scrollRef = useRef(null);

  // Convert UCI moves to SAN notation
  const getMovesWithSAN = () => {
    const chess = new Chess();
    const movePairs = [];
    
    for (let i = 0; i < moves.length; i += 2) {
      const pair = {
        number: Math.floor(i / 2) + 1,
        white: null,
        black: null,
        whiteIndex: i,
        blackIndex: i + 1
      };
      
      // White's move
      if (moves[i]) {
        try {
          const move = chess.move({ from: moves[i].slice(0, 2), to: moves[i].slice(2, 4), promotion: moves[i][4] });
          pair.white = move.san;
        } catch (e) {
          pair.white = moves[i];
        }
      }
      
      // Black's move
      if (moves[i + 1]) {
        try {
          const move = chess.move({ from: moves[i + 1].slice(0, 2), to: moves[i + 1].slice(2, 4), promotion: moves[i + 1][4] });
          pair.black = move.san;
        } catch (e) {
          pair.black = moves[i + 1];
        }
      }
      
      movePairs.push(pair);
    }
    
    return movePairs;
  };

  const movePairs = getMovesWithSAN();

  // Auto-scroll to latest move
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [moves.length]);

  return (
    <div className="h-full flex flex-col" data-testid="move-history">
      <div className="p-4 border-b border-white/5">
        <h2 className="font-heading text-sm font-semibold text-white/80 uppercase tracking-widest">
          Move History
        </h2>
        <p className="text-xs text-white/40 mt-1">
          {moves.length} moves played
        </p>
      </div>
      
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-4">
          {movePairs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-white/30 text-sm">No moves yet</p>
              <p className="text-white/20 text-xs mt-1">
                {playerColor === 'white' ? 'Make your opening move' : 'Waiting for opponent'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {movePairs.map((pair) => (
                <div 
                  key={pair.number}
                  className="flex items-center font-mono text-sm"
                >
                  <span className="w-8 text-white/30 text-right pr-2">
                    {pair.number}.
                  </span>
                  <button
                    type="button"
                    onClick={() => pair.white && onMoveSelect?.(pair.whiteIndex)}
                    disabled={!pair.white}
                    className={`flex-1 px-2 py-1 rounded-sm text-left transition-colors disabled:cursor-default ${
                      currentMoveIndex === pair.whiteIndex 
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                        : playerColor === 'white' 
                          ? 'text-white hover:bg-white/5' 
                          : 'text-white/60 hover:bg-white/5'
                    }`}
                    data-testid={`move-${pair.whiteIndex}`}
                  >
                    {pair.white || '...'}
                  </button>
                  <button
                    type="button"
                    onClick={() => pair.black && onMoveSelect?.(pair.blackIndex)}
                    disabled={!pair.black}
                    className={`flex-1 px-2 py-1 rounded-sm text-left transition-colors disabled:cursor-default ${
                      currentMoveIndex === pair.blackIndex 
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                        : playerColor === 'black' 
                          ? 'text-white hover:bg-white/5' 
                          : 'text-white/60 hover:bg-white/5'
                    }`}
                    data-testid={`move-${pair.blackIndex}`}
                  >
                    {pair.black || ''}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
